import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
// User profile is not sensitive — cache in localStorage purely to avoid a
// blank navbar flicker on first paint. The auth token itself lives in an
// httpOnly cookie the browser handles automatically.
const USER_KEY = 'la_user';
export const JUST_AUTHENTICATED_FLAG = 'la_just_authenticated';

// The CSRF token is kept in-memory (not localStorage). The server also sets
// a `csrf_token` cookie on the API origin, which the browser sends back
// automatically with `withCredentials`. We keep the value here purely so the
// SPA can echo it in the `X-CSRF-Token` header — this works even when the
// SPA lives on a different registrable domain from the API (cross-origin),
// where `document.cookie` cannot see the API's cookies.
let _csrfToken = null;
export function setCsrfToken(token) { if (token) _csrfToken = token; }
export function getCsrfToken() { return _csrfToken; }

const debug = (...args) => {
  if (typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('debug') === '1') {
    // eslint-disable-next-line no-console
    console.debug('[auth]', ...args);
  }
};

// Shared axios instance: sends cookies + echoes CSRF from the in-memory token.
export const api = axios.create({ baseURL: API, withCredentials: true });
api.interceptors.request.use((config) => {
  const method = (config.method || 'get').toUpperCase();
  if (['POST', 'PUT', 'PATCH', 'DELETE'].includes(method)) {
    if (_csrfToken) config.headers['X-CSRF-Token'] = _csrfToken;
  }
  return config;
});
// Any response that carries a fresh CSRF token (login/signup/reset/csrf) updates
// the in-memory value automatically.
api.interceptors.response.use((response) => {
  const t = response?.data?.csrf_token;
  if (t) _csrfToken = t;
  return response;
});

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem(USER_KEY) || 'null'); }
    catch { return null; }
  });
  const [ready, setReady] = useState(false);

  const setSession = useCallback((_ignoredToken, newUser) => {
    if (newUser) {
      localStorage.setItem(USER_KEY, JSON.stringify(newUser));
      setUser(newUser);
    }
    setReady(true);
    debug('setSession user=', newUser?.email);
  }, []);

  const clearSession = useCallback(async () => {
    debug('clearSession');
    try { await api.post('/auth/logout'); } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('logout failed (non-fatal):', err?.response?.status || err?.message);
    }
    _csrfToken = null;
    localStorage.removeItem(USER_KEY);
    sessionStorage.removeItem(JUST_AUTHENTICATED_FLAG);
    setUser(null);
    // Immediately fetch a fresh CSRF token so the sign-in form can post again.
    try { await api.get('/auth/csrf'); }
    catch (err) {
      // Non-fatal — the next form submit will re-trigger a CSRF bootstrap.
      // eslint-disable-next-line no-console
      console.warn('post-logout csrf fetch failed (non-fatal):', err?.response?.status || err?.message);
    }
  }, []);

  const refreshMe = useCallback(async () => {
    try {
      const { data } = await api.get('/auth/me');
      localStorage.setItem(USER_KEY, JSON.stringify(data));
      setUser(data);
      return data;
    } catch (err) {
      if (err?.response?.status === 401) {
        localStorage.removeItem(USER_KEY);
        setUser(null);
      }
      return null;
    }
  }, []);

  // On mount: fetch a CSRF token (stored in memory + cookie), then validate any
  // existing session via /auth/me. Both are idempotent GETs so it's fine if
  // StrictMode dev-mode fires the effect twice.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try { await api.get('/auth/csrf'); }
      catch (err) { debug('csrf bootstrap failed', err?.message); }
      if (cancelled) return;
      await refreshMe();
      if (!cancelled) setReady(true);
    })();
    return () => { cancelled = true; };
  }, [refreshMe]);

  const value = useMemo(
    () => ({ user, ready, setSession, clearSession, refreshMe, authAxios: api, API, token: null }),
    [user, ready, setSession, clearSession, refreshMe],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
